import { createContext, ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { shopAxios, userLogout } from '../services/api.shop';
import { ShopAuthContextType, ShopRegisterResult, ShopUserInfo } from '../types/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const defaultAuthValue: ShopAuthContextType = {
    user: null,
    isAuthenticated: false,
    isAuthReady: false,
    loading: true,
    login: async () => { throw new Error('Not in provider'); },
    loginWithGoogle: async () => { throw new Error('Not in provider'); },
    register: async () => {
        throw new Error('Not in provider');
    },
    logout: async () => { throw new Error('Not in provider'); },
};

export const ShopAuthContext = createContext<ShopAuthContextType>(defaultAuthValue);

export const ShopAuthProvider = ({ children }: { children: ReactNode }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Remove after testing
    useEffect(() => { console.count("ShopAuthProvider rendered"); }, []);

    const { data, isLoading: loading, error } = useQuery({
        queryKey: ['user'],
        queryFn: async (): Promise<ShopUserInfo | null> => {
            console.log(" Auth queryFn executed"); // Keep temporarily
            try {
                const res = await shopAxios.get('/auth/me');
                return res.data;
            } catch (err: unknown) {
                if (
                    err &&
                    typeof err === 'object' &&
                    err !== null &&
                    'response' in err &&
                    (err.response as { status?: number }).status === 401
                ) {
                    return null;
                }
                throw err;
            }
        },
        staleTime: 15 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        networkMode: 'offlineFirst',
    });

    const user = data ?? null;
    const isAuthenticated = !!user;
    const isAuthReady = !loading;

    if (error) console.error('Auth query error:', error);

    // User-specific query keys that must be cleared on login/logout to prevent cross-user data leakage (e.g. myProducts showing another user's items)
    const clearUserPrivateCaches = () => {
        // Remove both legacy keys and current userId-suffixed private keys.
        // This + per-user queryKey injection in useProtectedQuery guarantees ZERO bleed
        // between accounts even during SPA logins, refreshes, or timing races.
        ['myProducts', 'myOrders', 'userProfile', 'myChildren', 'notifications', 'favourites'].forEach((base) => {
            queryClient.removeQueries({ queryKey: [base], exact: false });
            // Also aggressively remove any keys that start with the base (covers suffixed)
            queryClient.removeQueries({
                predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === base
            });
        });

        // Product DTOs embed viewer-specific reservedByMe — must not survive auth switch
        // (home/search/detail would keep "You started checkout…" until hard refresh).
        ['products', 'search', 'product', 'recommendations'].forEach((base) => {
            queryClient.removeQueries({
                predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === base,
            });
        });
    };

    // Additional safety: watch for authenticated user ID changes (covers SPA timing windows,
    // external token changes, refresh failures that update the 'user' query, etc.)
    // Ensures private caches (even with userId-keyed) are cleaned when switching accounts.
    const prevUserIdRef = useRef<string | null>(null);
    useEffect(() => {
        const currentId = user?.id ?? null;
        if (prevUserIdRef.current !== null && prevUserIdRef.current !== currentId) {
            // Switched accounts (or logged out) within the session -> clear old user's private data
            clearUserPrivateCaches();
        }
        prevUserIdRef.current = currentId;
    }, [user?.id]);

    const login = async (email: string, password: string) => {
        const { data } = await shopAxios.post('/auth/user-login', { email, password });

        // Optimistically update cache with user from login response
        if (data?.user) {
            queryClient.setQueryData(['user'], data.user);
        } else {
            // Fallback: refetch from /auth/me
            await queryClient.invalidateQueries({ queryKey: ['user'] });
        }

        // Prevent data cross-pollution: clear any cached private data belonging to previous user
        clearUserPrivateCaches();

        // Navigation after login is handled by the Login page (returnUrl / location.state.from).
        // Avoid hard-coding /home so protected deep-links work after mobile bottom-nav → login.
    };

    const loginWithGoogle = async (idToken: string, preferredLanguage?: string) => {
        const { data } = await shopAxios.post('/auth/google', {
            idToken,
            preferredLanguage: preferredLanguage || undefined,
        });

        if (data?.user) {
            queryClient.setQueryData(['user'], data.user);
        } else {
            await queryClient.invalidateQueries({ queryKey: ['user'] });
        }

        clearUserPrivateCaches();
    };

    const register = async (
        email: string,
        userName: string,
        password: string,
        preferredLanguage?: string,
        firstName?: string,
        lastName?: string
    ): Promise<ShopRegisterResult> => {
        // Shop registration does NOT set shopToken cookies until email is confirmed.
        const { data } = await shopAxios.post('/auth/user-register', {
            email,
            userName,
            password,
            firstName,
            lastName,
            preferredLanguage: preferredLanguage || undefined,
        });

        // Ensure we do not keep a stale session from a previous user
        queryClient.setQueryData(['user'], null);
        clearUserPrivateCaches();

        const result: ShopRegisterResult = {
            requiresEmailConfirmation: data?.requiresEmailConfirmation !== false,
            email: data?.email ?? email,
            message: data?.message,
        };

        if (result.requiresEmailConfirmation) {
            // replace: avoid re-POST register form via Back
            navigate('/check-email', { replace: true, state: { email: result.email } });
        }

        return result;
    };

    const logout = async () => {
        try {
            await userLogout();
        } catch (err) {
            console.warn('Logout failed');
        }
        queryClient.setQueryData(['user'], null); // Clear cache immediately
        queryClient.invalidateQueries({ queryKey: ['user'] });

        // Clear private user data to avoid leakage if another account logs in later in same browser session
        clearUserPrivateCaches();

        // replace: leave private pages (account, etc.) off the Back stack after sign-out
        navigate('/login', { replace: true });
    };

    return (
        <ShopAuthContext.Provider value={{
            user, isAuthenticated, isAuthReady, loading,
            login, loginWithGoogle, register, logout
        }}>
            {children}
        </ShopAuthContext.Provider>
    );
};